import { useState } from "react";
import { colors, customEvent, HStack, VStack } from "goi_common";

import { BugoShareChoiceModalContent } from "./BugoShareChoiceModalContent";
import {
  BankAccountResponse,
  BankAccountShareMode,
  ObituarySanju,
} from "@/containers/BugoContainer/type";

/**
 * 특정 상주의 조문객 링크에 계좌가 하나라도 노출되는지.
 * 그 상주의 공유 범위 설정이 노출 범위를 정한다.
 * - OWN_ONLY("자기 계좌만"): 본인 계좌만 노출
 * - ALL_SANGJUS("전체 공유"): 부고에 등록된 모든 상주 계좌 노출
 */
export const hasVisibleBankAccount = (
  sangjus: ObituarySanju[],
  target: {
    bank_account?: BankAccountResponse | null;
    bank_account_share_mode?: BankAccountShareMode | null;
  }
) =>
  target.bank_account_share_mode === "ALL_SANGJUS"
    ? !!target.bank_account || sangjus.some((sangju) => !!sangju.bank_account)
    : !!target.bank_account;

/**
 * "이 부고에서는 다시 보지 않기" 상태. 부고 단위로 이 브라우저에만 저장한다.
 * 서버에 저장하지 않으므로 기기/브라우저가 바뀌면 안내가 다시 뜬다.
 */
const EMPTY_ACCOUNT_CONFIRM_DISMISS_KEY = "bugo-empty-account-confirm-dismiss:";

export const isEmptyAccountConfirmDismissed = (obituaryKey?: string) => {
  if (!obituaryKey) return false;
  try {
    return (
      localStorage.getItem(EMPTY_ACCOUNT_CONFIRM_DISMISS_KEY + obituaryKey) ===
      "1"
    );
  } catch {
    return false;
  }
};

export const setEmptyAccountConfirmDismissed = (
  obituaryKey: string | undefined,
  dismissed: boolean
) => {
  if (!obituaryKey) return;
  try {
    const key = EMPTY_ACCOUNT_CONFIRM_DISMISS_KEY + obituaryKey;
    if (dismissed) localStorage.setItem(key, "1");
    else localStorage.removeItem(key);
  } catch {}
};

/**
 * 계좌가 하나도 없는 상태로 부고를 전달하려는 경우, 공유 모달 앞에 확인 단계를 둔다.
 * 계좌를 등록·설정할 수 있는 작성자/상주 화면에서만 렌더되므로 조문객에게는 뜨지 않는다.
 */
export const ShareChoiceWithAccountGuard = ({
  onClose,
  needsEmptyAccountConfirm,
  onDismissEmptyAccountConfirm,
  onClickAccountSetting,
  type,
  obituaryKey,
}: {
  onClose: () => void;
  needsEmptyAccountConfirm: boolean;
  onDismissEmptyAccountConfirm: () => void;
  onClickAccountSetting: () => void;
  type: "writer" | "sangju";
  obituaryKey?: string;
}) => {
  const [ignoredEmptyAccount, setIgnoredEmptyAccount] = useState(false);

  if (needsEmptyAccountConfirm && !ignoredEmptyAccount) {
    return (
      <EmptyAccountConfirmContent
        onClickAccountSetting={onClickAccountSetting}
        onConfirm={(doNotShowAgain) => {
          if (doNotShowAgain) onDismissEmptyAccountConfirm();
          setIgnoredEmptyAccount(true);
        }}
      />
    );
  }

  return (
    <BugoShareChoiceModalContent
      onClose={onClose}
      type={type}
      obituaryKey={obituaryKey}
    />
  );
};

/** 계좌 없이 전달할지 묻는 확인 화면. 모달 안에서도, 공유 실행 직전에도 쓴다. */
export const EmptyAccountConfirmContent = ({
  onClickAccountSetting,
  onConfirm,
}: {
  onClickAccountSetting: () => void;
  onConfirm: (doNotShowAgain: boolean) => void;
}) => {
  const [doNotShowAgain, setDoNotShowAgain] = useState(false);

  return (
    <VStack
      background={colors.white}
      gap="20px"
      css={{
        borderRadius: "20px",
        padding: "28px 20px 20px",
        textAlign: "center",
      }}
    >
      <VStack gap="12px">
        <div className="subtitle_21_sb gray800">계좌 없이 전달할까요?</div>
        <div
          className="body3_m gray500"
          css={{ lineHeight: "1.6", whiteSpace: "pre-wrap" }}
        >
          {
            "현재 부고장에 노출되는\n부의금 계좌가 없습니다.\n이대로 전달하면 받은 분은\n계좌 정보를 확인할 수 없습니다."
          }
        </div>
      </VStack>
      <HStack
        as="button"
        gap="8px"
        alignItems="center"
        onClick={() => setDoNotShowAgain((prev) => !prev)}
        css={{ justifyContent: "center", padding: "2px 0" }}
      >
        <div
          css={{
            width: "18px",
            height: "18px",
            borderRadius: "4px",
            border: `1.5px solid ${
              doNotShowAgain ? colors.warmGray850 : colors.gray300
            }`,
            background: doNotShowAgain ? colors.warmGray850 : colors.white,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {doNotShowAgain && (
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none">
              <path
                d="M2.5 7L5.5 10L11.5 4"
                stroke={colors.white}
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
        <span className="body3_m gray500">이 부고에서는 다시 보지 않기</span>
      </HStack>
      <VStack gap="8px">
        <button
          className="subtitle_16_sb"
          onClick={() => {
            customEvent({
              path: "bugo_detail",
              trigger_and_target: "btn_check_account-bugo_share",
            });
            onClickAccountSetting();
          }}
          css={{
            width: "100%",
            padding: "14px",
            borderRadius: "10px",
            background: colors.white,
            border: `1px solid ${colors.gray200}`,
            color: colors.warmGray850,
          }}
        >
          계좌 등록 하러가기
        </button>
        <button
          className="subtitle_16_sb white"
          onClick={() => {
            customEvent({
              path: "bugo_detail",
              trigger_and_target: "btn_share_anyway-bugo_share",
              properties: { do_not_show_again: doNotShowAgain },
            });
            onConfirm(doNotShowAgain);
          }}
          css={{
            width: "100%",
            padding: "14px",
            borderRadius: "10px",
            background: colors.warmGray850,
          }}
        >
          계좌 없이 전달하기
        </button>
      </VStack>
    </VStack>
  );
};
